import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, type MotionValue, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { AlertCircle, ArrowRight, Bot, Check, ChevronDown, Clock, Database, FileText, Gavel, History, LinkIcon, LockKeyhole, MailCheck, MessageSquareText, Plug, Sparkles, Video, Wallet, Webhook, X } from 'lucide-react';
import { HOME_AGENT_DEFINITION } from '@/data/marketingContent';
import { cn } from '@/lib/utils';
import { EASE, Reveal, SectionHeading, SpotlightCard } from './primitives';
import { InViewRing } from './StoryVisuals';
import { SKIP_ENTRANCE } from './motionPrefs';

/* ───────────────────────── Problem ───────────────────────── */

function VolumeIllustration() {
  const reduced = useReducedMotion();
  return (
    <div className="relative h-40" aria-hidden>
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 h-[92px] w-[132px] rounded-xl border border-bv-blue-line bg-white p-2.5 shadow-bv-card"
          style={{ marginLeft: -66, marginTop: -46 }}
          initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: -60, rotate: 0 }}
          whileInView={{ opacity: 1, y: (5 - i) * -5, x: (i - 2.5) * 10, rotate: (i - 2.5) * 5 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: 0.15 * i, type: 'spring', stiffness: 160, damping: 18 }}
        >
          <div className="flex items-center gap-1.5"><span className="h-4 w-4 rounded-full bg-bv-blue-wash" /><span className="h-1.5 w-12 rounded-full bg-bv-blue-line" /></div>
          <div className="mt-2.5 space-y-1.5"><div className="h-1.5 w-full rounded-full bg-bv-blue-line" /><div className="h-1.5 w-4/5 rounded-full bg-bv-blue-line" /><div className="h-1.5 w-3/5 rounded-full bg-bv-blue-line" /></div>
        </motion.div>
      ))}
      <span className="absolute right-2 top-2 rounded-full bg-bv-ink px-2.5 py-1 font-display text-xs font-semibold text-white">+214</span>
    </div>
  );
}

function ClockIllustration() {
  const reduced = useReducedMotion();
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex h-40 items-center justify-center" aria-hidden>
      <svg width="132" height="132" className="-rotate-90">
        <circle cx="66" cy="66" r={r} fill="none" stroke="hsl(var(--blue-line))" strokeWidth="10" />
        <motion.circle cx="66" cy="66" r={r} fill="none" stroke="url(#clockGradient)" strokeWidth="10" strokeLinecap="round" strokeDasharray={c} initial={reduced || SKIP_ENTRANCE ? false : { strokeDashoffset: c }} whileInView={{ strokeDashoffset: c * 0.22 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 2, ease: EASE }} />
        <defs><linearGradient id="clockGradient"><stop offset="0%" stopColor="hsl(var(--hero-highlight-start))" /><stop offset="100%" stopColor="hsl(var(--hero-highlight-end))" /></linearGradient></defs>
      </svg>
      <div className="absolute text-center"><Clock className="mx-auto h-5 w-5 text-bv-coral" /><p className="mt-1 font-display text-lg font-semibold text-bv-ink">48h</p><p className="text-[11px] font-medium text-bv-mute">to shortlist</p></div>
    </div>
  );
}

function CostIllustration() {
  const reduced = useReducedMotion();
  const bars = [30, 42, 55, 64, 78, 92];
  return (
    <div className="flex h-40 items-end justify-center gap-2.5 pb-3" aria-hidden>
      {bars.map((h, i) => (
        <div key={i} className="flex flex-col items-center gap-1.5">
          <motion.div className={cn('w-7 rounded-t-lg', i === bars.length - 1 ? 'bv-btn-gradient' : 'bg-bv-blue-line')} initial={reduced || SKIP_ENTRANCE ? false : { height: 0 }} whileInView={{ height: h + 20 }} viewport={{ once: true, margin: '-60px' }} transition={{ delay: 0.1 * i, duration: 0.8, ease: EASE }} />
          <span className="text-[10px] font-medium text-bv-mute">{['J', 'F', 'M', 'A', 'M', 'J'][i]}</span>
        </div>
      ))}
    </div>
  );
}

const problems = [
  { title: 'The volume', body: 'A single advertised role can attract hundreds of applications. Many will not fit the brief, but someone still has to open and assess every one.', Illustration: VolumeIllustration },
  { title: 'The clock', body: 'Clients expect a shortlist in days. Time spent reading resumes comes directly out of time for client and candidate relationships.', Illustration: ClockIllustration },
  { title: 'The cost', body: 'Screening is the part of agency delivery that grows with application volume while producing no billable hours.', Illustration: CostIllustration },
];

export function ProblemSection() {
  return (
    <section className="bg-bv-base py-24 lg:py-32" aria-labelledby="problem-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading id="problem-title" eyebrow="The screening problem" title={<>Your recruiters are reading applications <span className="text-bv-mute">instead of placing candidates</span></>} description="Application volume per role has risen sharply. Agency delivery teams have not. The screening layer has quietly become the largest unbilled cost in a recruitment business." />
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {problems.map(({ title, body, Illustration }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <SpotlightCard className="h-full p-6 sm:p-7">
                <div className="rounded-2xl bg-bv-blue-wash/70"><Illustration /></div>
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-[-0.02em] text-bv-ink">{title}</h3>
                <p className="mt-3 text-[16px] leading-7 text-bv-ink2">{body}</p>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="font-display text-xl font-medium leading-8 text-bv-ink">
            <Link to="/guides/what-is-an-ai-recruiting-agent" className="bv-link-underline text-bv-blue">An AI recruiting agent</Link> absorbs that layer — here’s what that means.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────── Definition (GEO) ───────────────────────── */

export function AgentDefinition() {
  const reduced = useReducedMotion();
  const atsItems = ['Stores applications', 'Waits for a recruiter to open each one', 'Screening happens outside the system'];
  const agentItems = ['Reads and scores every application', 'Sends interview invitations by your rules', 'Prepares evidence for recruiter review'];
  return (
    <section className="relative overflow-hidden border-y border-bv-blue-line bg-white py-24 lg:py-32" aria-labelledby="definition-title">
      <div className="mx-auto grid max-w-[1240px] gap-14 px-5 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:px-8">
        <div>
          <SectionHeading id="definition-title" eyebrow="Definition" title="What is an AI recruiting agent?" />
          <Reveal delay={0.1}>
            <div className="mt-7 rounded-2xl border-l-4 border-bv-coral bg-bv-orange-soft/50 p-6">
              <p className="text-[13px] font-semibold uppercase tracking-wider text-bv-coral">The short answer</p>
              <p className="mt-2 text-lg leading-8 text-bv-ink">{HOME_AGENT_DEFINITION}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <dl className="mt-8 grid gap-6 sm:grid-cols-3">
              {[['Does on its own', 'Reads, scores, invites, records answers, prepares evidence.'], ['Never does', 'Makes the final hiring decision or contacts your client.'], ['Why agencies use one', 'Recruiters get their time back for candidates and clients.']].map(([term, detail]) => (
                <div key={term}><dt className="font-display text-base font-semibold text-bv-ink">{term}</dt><dd className="mt-1.5 text-[15px] leading-6 text-bv-ink2">{detail}</dd></div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/guides/what-is-an-ai-recruiting-agent" className="group mt-8 inline-flex items-center gap-1.5 font-semibold text-bv-blue">Read the full guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-bv-blue-line bg-bv-blue-wash/60 p-6">
              <div className="flex items-center gap-2.5"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-bv-mute shadow-bv-card"><Database className="h-5 w-5" /></span><p className="font-display text-lg font-semibold text-bv-ink2">Applicant tracking system</p></div>
              <ul className="mt-6 space-y-3">{atsItems.map(item => <li key={item} className="flex gap-2.5 text-[15px] leading-6 text-bv-ink2"><X className="mt-1 h-4 w-4 shrink-0 text-bv-mute" />{item}</li>)}</ul>
              <p className="mt-6 text-sm font-semibold text-bv-mute">Organises the work</p>
            </div>
            <div className="relative overflow-hidden rounded-3xl bg-bv-ink p-6 text-white shadow-bv-deep">
              <div aria-hidden className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[hsl(var(--hero-highlight-start)/0.35)] blur-3xl" />
              <div className="relative flex items-center gap-2.5"><span className="bv-btn-gradient flex h-10 w-10 items-center justify-center rounded-xl text-white"><Bot className="h-5 w-5" /></span><p className="font-display text-lg font-semibold">AI recruiting agent</p></div>
              <ul className="relative mt-6 space-y-3">
                {agentItems.map((item, i) => (
                  <motion.li key={item} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.15, ease: EASE }} className="flex gap-2.5 text-[15px] leading-6 text-white/85">
                    <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-bv-success"><Check className="h-2.5 w-2.5" strokeWidth={3} /></span>{item}
                  </motion.li>
                ))}
              </ul>
              <p className="relative mt-6 text-sm font-semibold text-bv-blue-bright">Does the work — you decide</p>
            </div>
            <span aria-hidden className="absolute left-1/2 top-1/2 hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-bv-blue-line bg-white text-bv-ink shadow-bv-window sm:flex"><ArrowRight className="h-4 w-4" /></span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────── Feature bento ───────────────────────── */

function SortingVisual() {
  const reduced = useReducedMotion();
  const columns = [
    { label: 'Strong match', tone: 'bg-bv-success-soft text-bv-success', people: ['MC', 'AR', 'SA'] },
    { label: 'Worth a look', tone: 'bg-bv-blue-wash text-bv-blue', people: ['DO', 'LM'] },
    { label: 'Not evidenced', tone: 'bg-bv-orange-soft text-bv-coral', people: ['PN', 'TB', 'JK', 'RS'] },
  ];
  return (
    <div className="grid grid-cols-3 gap-3" aria-hidden>
      {columns.map((col, ci) => (
        <div key={col.label} className="rounded-2xl border border-bv-blue-line bg-white p-3">
          <p className={cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', col.tone)}>{col.label}</p>
          <div className="mt-3 space-y-2">
            {col.people.map((p, pi) => (
              <motion.div key={p} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, y: -24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ delay: 0.2 + ci * 0.25 + pi * 0.12, type: 'spring', stiffness: 220, damping: 20 }} className="flex items-center gap-2 rounded-lg border border-bv-blue-line bg-bv-blue-wash/40 p-1.5">
                <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold', col.tone)}>{p}</span>
                <span className="h-1.5 flex-1 rounded-full bg-bv-blue-line" />
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniInterview() {
  const reduced = useReducedMotion();
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[radial-gradient(circle_at_50%_30%,hsl(var(--navy-soft)),hsl(var(--ink)))] p-4" aria-hidden>
      <div className="flex items-center justify-between text-[11px] font-semibold text-white"><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />Recording</span><span className="rounded-full bg-white/10 px-2 py-0.5">Q2 · 01:30 left</span></div>
      <div className="mt-6 flex h-12 items-center justify-center gap-[3px]">
        {Array.from({ length: 24 }).map((_, i) => <span key={i} className="bv-eq-bar w-[3px] rounded-full bg-bv-blue-bright" style={{ height: 6 + ((i * 29) % 36), animationDuration: `${0.7 + ((i * 11) % 6) / 10}s`, animationDelay: `${-((i * 7) % 10) / 10}s` }} />)}
      </div>
      <p className="mt-5 text-center text-[13px] font-medium text-white/80">Same questions. Same time limit. Every candidate.</p>
    </div>
  );
}

function EvidenceLinkVisual() {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="flex items-center justify-between rounded-xl border border-bv-blue-line bg-white p-3"><div><p className="text-[11px] text-bv-mute">Criterion</p><p className="text-[13px] font-semibold text-bv-ink">Enterprise pipeline ownership</p></div><AlertCircle className="h-4 w-4 text-bv-coral" /></div>
      <div className="ml-4 border-l-2 border-dashed border-bv-coral/50 pl-3"><p className="rounded-lg bg-bv-orange-soft px-3 py-2 text-[12px] font-medium text-bv-ink">No resume line evidences this requirement</p></div>
      <div className="flex items-center justify-between rounded-xl border border-bv-blue-line bg-white p-3"><div><p className="text-[11px] text-bv-mute">Criterion</p><p className="text-[13px] font-semibold text-bv-ink">Team coaching</p></div><Check className="h-4 w-4 text-bv-success" /></div>
    </div>
  );
}

function CareerPageVisual() {
  return (
    <div className="overflow-hidden rounded-2xl border border-bv-blue-line bg-white" aria-hidden>
      <div className="flex items-center gap-2 border-b border-bv-blue-line bg-bv-blue-wash/60 px-3 py-2"><span className="h-2 w-2 rounded-full bg-bv-blue-line" /><span className="h-2 w-2 rounded-full bg-bv-blue-line" /><span className="ml-1 truncate rounded bg-white px-2 py-0.5 text-[10px] text-bv-mute">careers.youragency.com</span></div>
      <div className="p-3">
        <div className="flex items-center gap-2"><span className="bv-btn-gradient h-6 w-6 rounded-md" /><span className="text-[12px] font-bold text-bv-ink">Your Agency</span></div>
        <div className="mt-3 space-y-1.5">{['Senior Account Manager', 'Operations Manager'].map(role => <div key={role} className="flex items-center justify-between rounded-lg border border-bv-blue-line px-2.5 py-2 text-[12px] font-medium text-bv-ink">{role}<span className="rounded bg-bv-ink px-1.5 py-0.5 text-[10px] font-semibold text-white">Apply</span></div>)}</div>
      </div>
    </div>
  );
}

function ShareVisual() {
  return (
    <div className="space-y-2" aria-hidden>
      <div className="flex items-center gap-2 rounded-xl border border-bv-blue-line bg-white px-3 py-2.5 text-[12px] text-bv-ink"><LinkIcon className="h-3.5 w-3.5 text-bv-blue" /><span className="truncate">beatview.ai/share/daniel-okafor</span></div>
      <div className="flex items-center gap-2 rounded-xl border border-bv-blue-line bg-white px-3 py-2.5 text-[12px] text-bv-mute"><LockKeyhole className="h-3.5 w-3.5 text-bv-coral" />•••••••••••</div>
      <div className="flex items-start gap-2 rounded-xl bg-bv-blue-wash px-3 py-2.5 text-[12px] text-bv-ink"><MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bv-blue" />Client note returned to the record</div>
    </div>
  );
}

export function TimelineVisual() {
  const reduced = useReducedMotion();
  const events = [{ icon: FileText, label: 'Application received', time: '09:12' }, { icon: Sparkles, label: 'Resume scored · 89%', time: '09:12' }, { icon: MailCheck, label: 'Interview invitation sent', time: '09:13' }, { icon: Video, label: 'Interview completed', time: 'Tue 18:40' }, { icon: Gavel, label: 'Recruiter moved to shortlist', time: 'Wed 10:05' }];
  return (
    <ol className="relative space-y-3" aria-hidden>
      <span className="absolute bottom-3 left-[15px] top-3 w-px bg-bv-blue-line" />
      {events.map((e, i) => (
        <motion.li key={e.label} initial={reduced || SKIP_ENTRANCE ? false : { opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ delay: 0.15 * i, ease: EASE }} className="relative flex items-center gap-3">
          <span className={cn('relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border', i === events.length - 1 ? 'border-transparent bg-bv-ink text-white' : 'border-bv-blue-line bg-white text-bv-blue')}><e.icon className="h-3.5 w-3.5" /></span>
          <span className="flex-1 text-[13px] font-medium text-bv-ink">{e.label}</span>
          <span className="text-[11px] tabular-nums text-bv-mute">{e.time}</span>
        </motion.li>
      ))}
    </ol>
  );
}

export function AtsHubVisual() {
  const nodes = [
    { label: 'Your ATS', sub: 'Stays your system of record', icon: Database, pos: 'left-0 top-2' },
    { label: 'Career page & job boards', sub: 'LinkedIn, Indeed & more', icon: FileText, pos: 'right-0 top-2' },
    { label: 'Zapier', sub: 'Most ATS platforms', icon: Plug, pos: 'left-0 bottom-2' },
    { label: 'Native integration', sub: 'Enabled on request', icon: Webhook, pos: 'right-0 bottom-2' },
  ];
  return (
    <div className="relative h-[200px]" aria-hidden>
      <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">
        {[[20, 18], [80, 18], [20, 82], [80, 82]].map(([x, y], i) => (
          <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="hsl(var(--blue))" strokeOpacity="0.35" strokeWidth="0.6" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" className="animate-dash" />
        ))}
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <span className="absolute inset-0 animate-ping rounded-2xl bg-bv-blue/20" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-bv-ink text-white shadow-bv-deep"><Bot className="h-6 w-6" /></span>
      </div>
      {nodes.map(n => (
        <div key={n.label} className={cn('absolute flex items-center gap-2 rounded-xl border border-bv-blue-line bg-white px-2.5 py-2 shadow-bv-card', n.pos)}>
          <n.icon className="h-4 w-4 text-bv-blue" />
          <div><p className="text-[12px] font-semibold leading-4 text-bv-ink">{n.label}</p><p className="text-[10px] text-bv-mute">{n.sub}</p></div>
        </div>
      ))}
    </div>
  );
}

const bento = [
  { title: 'Screens every application', body: 'Each resume is scored against the criteria your consultant configures, so strong matches surface first and nobody in the pile goes unread.', href: '/resume-screening', link: 'Resume screening', Visual: SortingVisual, span: 'lg:col-span-4' },
  { title: 'Structured AI video interviews', body: 'Candidates answer timed questions on their own schedule. No recruiter needed on the call.', href: '/ai-interviews', link: 'AI interviews', Visual: MiniInterview, span: 'lg:col-span-2' },
  { title: 'Evidence behind every score', body: 'See which resume line or answer supports — or weakens — each criterion.', href: '/features', link: 'Candidate evidence', Visual: EvidenceLinkVisual, span: 'lg:col-span-2' },
  { title: 'Your agency’s career page', body: 'Branded job pages on every plan. Share job links on LinkedIn, Indeed and any job board. Custom domain and email on Pro.', href: '/features', link: 'Branding', Visual: CareerPageVisual, span: 'lg:col-span-2' },
  { title: 'Client-safe sharing', body: 'Password-protected candidate links. Clients never see your workspace.', href: '/features', link: 'Client sharing', Visual: ShareVisual, span: 'lg:col-span-2' },
  { title: 'A full event history', body: 'Every automated step and every recruiter decision is recorded on the candidate record.', href: '/features', link: 'All features', Visual: TimelineVisual, span: 'lg:col-span-3' },
  { title: 'Works with most ATS platforms', body: 'No migration project. Connect your ATS through Zapier or ask us to enable a native integration. Job board links are supported.', href: '/integrations', link: 'See integrations', Visual: AtsHubVisual, span: 'lg:col-span-3' },
];

export function FeatureBento() {
  return (
    <section className="bg-bv-blue-wash py-24 lg:py-32" aria-labelledby="features-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading id="features-title" align="center" eyebrow="One agent, end to end" title={<>Everything between <span className="bv-hero-highlight">“application received”</span> and “shortlist sent”</>} description="Beatview replaces the stack of spreadsheets, calendar invites and unread CVs with one agent that does the repeatable work and keeps the evidence." />
        <div className="mt-14 grid gap-5 lg:grid-cols-6">
          {bento.map(({ title, body, href, link, Visual, span }, i) => (
            <Reveal key={title} delay={(i % 3) * 0.06} className={span}>
              <SpotlightCard className="flex h-full flex-col p-6 sm:p-7">
                <div className="flex-1"><Visual /></div>
                <h3 className="mt-6 font-display text-xl font-semibold tracking-[-0.01em] text-bv-ink">{title}</h3>
                <p className="mt-2 text-[15px] leading-6 text-bv-ink2">{body}</p>
                <Link to={href} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">{link}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></Link>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Score explainer ───────────────────────── */

export function ScoreExplainer() {
  const [open, setOpen] = useState(false);
  const interviewScores = [['Clarity', 94], ['Relevance', 91], ['Depth of knowledge', 88]] as const;
  return (
    <section className="bg-bv-base py-24 lg:py-32" aria-labelledby="scores-title">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <SectionHeading id="scores-title" eyebrow="Explainable scoring" title="See why candidates receive different scores" description="These examples show both outcomes: Priya Nair scored 68% because the resume did not evidence required enterprise pipeline ownership, while Aisha Rahman’s structured interview scored 91%." />
            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-3">
                {['Every score links to the resume line, interview answer or criterion that produced it', 'Lower scores stay visible — nothing is silently filtered out', 'Recruiters can open the reasoning, disagree and decide what happens next'].map(item => (
                  <li key={item} className="flex gap-3 text-[16px] leading-7 text-bv-ink2"><span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bv-success-soft text-bv-success"><Check className="h-3 w-3" strokeWidth={3} /></span>{item}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <Reveal>
              <article className="h-full rounded-3xl border border-bv-blue-line bg-white p-6 shadow-bv-window" aria-label="Example resume evidence for Priya Nair">
                <div className="flex items-center justify-between"><span className="rounded-md bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Example data</span><span className="text-[12px] font-medium text-bv-mute">Resume evidence</span></div>
                <div className="mt-5 flex items-center gap-4"><InViewRing score={68} size={72} stroke={6} tone="warn" /><div><p className="font-display text-xl font-semibold text-bv-ink">Priya Nair</p><p className="text-[14px] text-bv-ink2">Sales Development Manager</p></div></div>
                <ul className="mt-6 space-y-2.5">
                  <li className="flex gap-2.5 rounded-xl bg-bv-success-soft/60 p-3 text-[14px] font-medium leading-6 text-bv-ink"><Check className="mt-1 h-4 w-4 shrink-0 text-bv-success" />Strong team coaching evidence</li>
                  <li className="flex gap-2.5 rounded-xl bg-bv-orange-soft/70 p-3 text-[14px] font-medium leading-6 text-bv-ink"><AlertCircle className="mt-1 h-4 w-4 shrink-0 text-bv-coral" />No evidence of the required enterprise pipeline ownership</li>
                </ul>
                <p className="mt-5 flex items-center gap-2 text-[13px] text-bv-ink2"><History className="h-4 w-4 text-bv-blue" />The lower score stays visible for recruiter review.</p>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article className="h-full rounded-3xl border border-bv-blue-line bg-white p-6 shadow-bv-window" aria-label="Example interview result for Aisha Rahman">
                <div className="flex items-center justify-between"><span className="rounded-md bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral">Example data</span><span className="text-[12px] font-medium text-bv-mute">Interview · Q3 of 5</span></div>
                <div className="mt-5 flex items-center gap-4"><InViewRing score={91} size={72} stroke={6} tone="success" /><div><p className="font-display text-xl font-semibold text-bv-ink">Aisha Rahman</p><p className="text-[14px] text-bv-ink2">Handling account risk</p></div></div>
                <div className="mt-6 space-y-4">
                  {interviewScores.map(([label, score], i) => (
                    <div key={label}>
                      <div className="flex justify-between text-[14px] font-medium text-bv-ink2"><span>{label}</span><span className="tabular-nums">{score}%</span></div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-bv-success-soft"><motion.div className="h-full rounded-full bg-bv-success" initial={{ width: 0 }} whileInView={{ width: `${score}%` }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.15, duration: 1, ease: EASE }} /></div>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="mt-6 flex w-full items-center justify-between rounded-xl border border-bv-blue-line px-4 py-2.5 text-[14px] font-semibold text-bv-ink transition hover:bg-bv-blue-wash focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">
                  Read the AI assessment <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
                </button>
                <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden">
                  <p className="mt-3 rounded-xl bg-bv-blue-wash p-4 text-[14px] leading-6 text-bv-ink2">Strong answer: separates delivery risk from communication risk and names the next decision to agree with the client. Could add a concrete example.</p>
                </motion.div>
              </article>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Statement ───────────────────────── */

function Word({ children, progress, range, highlight }: { children: string; progress: MotionValue<number>; range: [number, number]; highlight: boolean }) {
  const opacity = useTransform(progress, range, [0.14, 1]);
  const y = useTransform(progress, range, [8, 0]);
  return <motion.span style={{ opacity, y }} className={cn('mr-[0.22em] inline-block', highlight ? 'bv-hero-highlight pb-[0.08em]' : 'text-bv-ink')}>{children}</motion.span>;
}

export function StatementReveal() {
  const ref = useRef<HTMLParagraphElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.9', 'end 0.5'] });
  const words = 'The agent screens. Your recruiters decide.'.split(' ');
  return (
    <section className="relative overflow-hidden bg-bv-base py-24 lg:py-40" aria-label="Beatview principle">
      <div aria-hidden className="bv-hero-grid absolute inset-0 opacity-60" />
      <p ref={ref} className="relative mx-auto max-w-[1040px] px-5 text-center font-display text-[42px] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-[88px]">
        {reduced
          ? <>The agent screens. <span className="bv-hero-highlight">Your recruiters decide.</span></>
          : words.map((w, i) => <Word key={w + i} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} highlight={i >= 3}>{w}</Word>)}
      </p>
      <Reveal className="relative mt-10 flex flex-wrap justify-center gap-3 px-5">
        {[{ icon: Bot, label: 'Agent prepares' }, { icon: FileText, label: 'Evidence attached' }, { icon: Gavel, label: 'Human decides' }, { icon: Wallet, label: 'Client sees only what you share' }].map(({ icon: Icon, label }) => (
          <span key={label} className="inline-flex items-center gap-2 rounded-full border border-bv-blue-line bg-white px-4 py-2 text-sm font-semibold text-bv-ink shadow-bv-card"><Icon className="h-4 w-4 text-bv-blue" />{label}</span>
        ))}
      </Reveal>
    </section>
  );
}
